/**
 * IDs from the schedule APIs are decimal strings. Keep them opaque: Amazon
 * profile and segment IDs regularly exceed JavaScript's safe integer range.
 */
export type BigintId = string

export type ScheduleKind = 'cron' | 'once'

export interface CronScheduleDefinition {
    kind: 'cron'
    timezone: string
    cron_expression: string
    run_once_at?: null
}

export interface OneTimeScheduleDefinition {
    kind: 'once'
    timezone: string
    cron_expression?: null
    run_once_at: string
}

export type ScheduleDefinition = CronScheduleDefinition | OneTimeScheduleDefinition
export type ScheduleDraft = ScheduleDefinition

/** A preview occurrence retains both the instant and the local wall-clock evidence. */
export interface ScheduleOccurrence {
    utc: string
    local: string
}

export interface RecipeSchedule {
    id: BigintId
    recipe_id: BigintId
    profile_id: BigintId
    timezone: string
    kind: ScheduleKind
    cron_expression: string | null
    run_once_at: string | null
    next_run_at: string | null
    last_scheduled_at: string | null
    created_at: string
    updated_at: string
}

export interface ScheduleDetails {
    schedule: RecipeSchedule
    occurrences: ScheduleOccurrence[]
    max_runs_per_day: number
    has_nearby_schedule: boolean
}

export interface ScheduleReadResponse {
    data: ScheduleDetails | null
}

export type SchedulePreviewRequest = ScheduleDefinition & {
    preview_count?: number
}

export interface SchedulePreview {
    occurrences: ScheduleOccurrence[]
    max_runs_per_day: number
    has_nearby_schedule: boolean
}

export interface SchedulePreviewResponse {
    data: SchedulePreview
}

export interface ScheduleTimezones {
    timezones: string[]
    default: string
    default_is_valid: boolean
}

export interface ScheduleTimezonesResponse {
    data: ScheduleTimezones
}

export type ScheduleRunStatus =
    | 'pending'
    | 'queued'
    | 'running'
    | 'completed'
    | 'failed'
    | 'skipped'

export type ScheduleRunSkipReason =
    | 'skipped_ad_type_unsupported'
    | 'skipped_disabled'
    | 'skipped_frequency_conflict'
    | 'skipped_no_active_subscription'
    | 'skipped_no_sp_access'
    | 'skipped_paused'
    | 'skipped_profile_unlinked'
    | 'skipped_rollout_not_allowlisted'
    | 'skipped_rollout_off'
    | 'skipped_schedule_changed'
    | 'skipped_shadow_mode'
    | 'skipped_stale_profile_update'
    | 'skipped_too_late'

/** The service intentionally permits new failure reasons without a UI deploy. */
export type ScheduleRunReasonCode = ScheduleRunSkipReason | string

export interface ScheduleRunSnapshot {
    kind: ScheduleKind
    timezone: string
    cron_expression: string | null
    run_once_at: string | null
}

export interface ScheduleRun {
    id: BigintId
    schedule_id: BigintId
    recipe_id: BigintId
    profile_id: BigintId
    recipe_name: string
    scheduled_for_at: string
    request_uuid: string | null
    status: ScheduleRunStatus
    reason_code: ScheduleRunReasonCode | null
    schedule_snapshot: ScheduleRunSnapshot
    freshness_observed_at: string | null
    claimed_at: string | null
    queued_at: string | null
    started_at: string | null
    finished_at: string | null
    result: unknown
    audit_log_id: BigintId | null
    created_at: string
    updated_at: string
}

export interface ScheduleRunHistory {
    items: ScheduleRun[]
    next_cursor: string | null
}

export interface ScheduleRunHistoryResponse {
    data: ScheduleRunHistory
}

export interface LatestScheduleRuns {
    items: ScheduleRun[]
    by_recipe_id: Record<BigintId, ScheduleRun>
}

export interface LatestScheduleRunsResponse {
    data: LatestScheduleRuns
}

export const scheduleDraftFromSchedule = (schedule: RecipeSchedule): ScheduleDraft =>
    schedule.kind === 'cron'
        ? {
              kind: 'cron',
              timezone: schedule.timezone,
              cron_expression: schedule.cron_expression ?? '',
          }
        : {
              kind: 'once',
              timezone: schedule.timezone,
              run_once_at: schedule.run_once_at ?? '',
          }
